// Pure-function port of the server-side allocation formula (see
// CcuSettingsService in TifoXRCoreWebAPI, and MULTIPLAYER_CAPACITY_IMPLEMENTATION_PLAN.md §2).
// Used client-side purely for an instant preview as the admin edits the 5 settings fields
// or changes the extra-share space selection — the server recomputes and persists the
// authoritative values on save, this never gets sent back to the API.


/**
 * @param {object} params
 * @param {number} params.globalMaxNormal    N
 * @param {number} params.globalMaxAbsolute  X
 * @param {number} params.activeSpaceCount   A — total non-deleted spaces (base + extra-share)
 * @param {number} params.extraShareSpaceCount E — subset of A currently selected for extra share
 * @returns {{ baseShareCcu: number, extraShareCcu: number, allocatedTotalCcu: number, normalUnallocatedCcu: number, absoluteUnallocatedCcu: number }}
 */
export function computeCcuAllocation({ globalMaxNormal, globalMaxAbsolute, activeSpaceCount, extraShareSpaceCount }) {
  const N = Number(globalMaxNormal) || 0;
  const X = Number(globalMaxAbsolute) || 0;
  const A = Number(activeSpaceCount) || 0;
  const E = Number(extraShareSpaceCount) || 0;

  // Round down, always — rounding up would let the total exceed globalMaxAbsolute.
  const baseShareCcu = A > 0 ? Math.floor(N / A) : 0;
  const extraShareCcu = E > 0 ? Math.max(0, Math.floor((X - N) / E)) : 0;

  const baseShareSpaceCount = A - E;
  const allocatedTotalCcu = (baseShareSpaceCount * baseShareCcu) + (E * (baseShareCcu + extraShareCcu));

  // Two distinct leftover pools, not one blended figure — both are just flooring remainders,
  // but of different ceilings:
  // - normalUnallocatedCcu: leftover under the Normal ceiling (N). Every space, extra-share
  //   or not, draws its base share from N, so this is N minus what base-share flooring
  //   actually handed out (A * baseShareCcu).
  // - absoluteUnallocatedCcu: leftover under the Absolute ceiling (X) once both base AND
  //   extra shares are handed out — the true "capacity left on the table" figure. This is
  //   what the old single unallocatedCcu field meant.
  const normalUnallocatedCcu = N - (A * baseShareCcu);
  const absoluteUnallocatedCcu = X - allocatedTotalCcu;

  return { baseShareCcu, extraShareCcu, allocatedTotalCcu, normalUnallocatedCcu, absoluteUnallocatedCcu };
}

// Per-space preview only — this is exactly the 3-line computation Unity does client-side
// with the published payload (see plan §2/§4: per-space allocatedCcu/allocatedRooms are
// deliberately not part of the wire contract). Used here purely so the CMS admin can see,
// before saving, what each space's allocation will become.
export function computeSpaceAllocation({ isExtraShare, baseShareCcu, extraShareCcu, maxPlayersPerRoom }) {
  const allocatedCcu = isExtraShare ? baseShareCcu + extraShareCcu : baseShareCcu;
  const P = Number(maxPlayersPerRoom) || 0;
  const allocatedRooms = allocatedCcu > 0 && P > 0 ? Math.ceil(allocatedCcu / P) : 0;
  return { allocatedCcu, allocatedRooms };
}
